import React from 'react';

import { useTranslation } from 'next-i18next';

import type { Props as ButtonProps } from 'src/components/ui/Button';
import Button from 'src/components/ui/Button';
import Icon from 'src/components/ui/Icon';

import useAlerts from 'src/hooks/useAlerts';

import { useAddContactMutation, useRemoveContactMutation } from 'src/hooks/__generated__/queries';
import type { User } from 'src/types/__generated__/graphql';
import { ContactStatus } from 'src/types/__generated__/graphql';

import useUser from 'src/hooks/useUser';
import useLoginModal from 'src/hooks/useLoginModal';
import useApolloCacheUpdate from 'src/hooks/useApolloCacheUpdate';
import { colours } from 'src/constants/colours';
import { AnalyticsEvent } from 'src/utils/analytics';
import { styledComponent } from 'src/utils/styled';
import useDynamicLogEvents from 'src/hooks/useDynamicLogEvents';

const PendingButton = styledComponent(Button)`
    color: ${colours.primary.main};
`;
interface Props {
    user: Pick<User, 'id' | 'firstName' | 'fullName'>;
    contactStatus?: ContactStatus | null;
    refetchQueries?: string[];
    buttonProps?: ButtonProps & { 'data-target-id'?: string };
    textOverride?: Partial<Record<ContactStatus, string>> & { default?: string };
}

const AddRemoveContact: React.FC<Props> = ({ user, contactStatus, refetchQueries, buttonProps, textOverride }) => {
    const { logEventDynamic } = useDynamicLogEvents();
    const { addAlert } = useAlerts();
    const { t } = useTranslation(['profile']);
    const { createAuthenticatedHandler } = useLoginModal();
    const { addContactInCache, removeContactInCache } = useApolloCacheUpdate();

    const currentUser = useUser();

    const isSelf = user.id === currentUser?.id;

    const combinedRefetchQueries = ['MyContacts', 'CurrentUserProfile', ...(refetchQueries ?? [])];

    const [addContactMutation] = useAddContactMutation({
        onError(err) {
            addAlert({
                message: err.message,
                type: 'error',
            });
        },
        onCompleted() {
            addContactInCache(user.id, contactStatus);
            addAlert({
                type: 'success',
                message: String(t('profile:addContactSuccess', { name: user.firstName ?? user.fullName })),
            });
        },
    });

    const [removeContactMutation] = useRemoveContactMutation({
        onError(err) {
            addAlert({
                message: err.message,
                type: 'error',
            });
        },
        onCompleted() {
            removeContactInCache(user.id, contactStatus);
        },
    });
    const addContact = async () => {
        await addContactMutation({
            variables: {
                userId: user.id,
            },
            refetchQueries: combinedRefetchQueries,
            awaitRefetchQueries: true,
        });

        logEventDynamic(AnalyticsEvent.Invite);
    };
    const handleAddContact = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        e.stopPropagation();
        await createAuthenticatedHandler(addContact);
    };

    const removeContact = async () => {
        await removeContactMutation({
            variables: {
                userId: user.id,
            },
            refetchQueries: combinedRefetchQueries,
            awaitRefetchQueries: true,
        });
    };

    const handleRemoveContact = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        e.stopPropagation();
        await createAuthenticatedHandler(removeContact);
    };

    if (isSelf) {
        return null;
    }

    const translationKey = textOverride?.[contactStatus ?? 'default'];

    // Add contact
    if (!contactStatus || [ContactStatus.Uninvited, ContactStatus.ToRespond].includes(contactStatus)) {
        return (
            <Button
                variant="outlined"
                startIcon={<Icon style={{ margin: '-0.125rem' }} name="AddUser" />}
                onClick={handleAddContact}
                {...buttonProps}
            >
                {t(translationKey ?? 'profile:addContact')}
            </Button>
        );
    }

    // Pending
    if (contactStatus === ContactStatus.Pending) {
        return (
            <PendingButton
                variant="outlined"
                startIcon={<Icon style={{ margin: '-0.125rem' }} name="Time" />}
                disabled
                {...buttonProps}
            >
                {t(translationKey ?? 'profile:pending')}
            </PendingButton>
        );
    }

    // Remove contact
    return (
        <Button color="inherit" onClick={handleRemoveContact} {...buttonProps}>
            {t(translationKey ?? 'profile:added')}
        </Button>
    );
};

export default AddRemoveContact;
